import React, {useState} from 'react';
import makeComp from '../util/profiler';
import {
  Button,
  Modal,
  Input,
  Header,
  Form,
  Checkbox,
  CheckboxProps,
  InputOnChangeData,
  ButtonProps,
} from 'semantic-ui-react';
import {useUpdateOrganizationSubscriptionMutation} from '../generated/graphql';
import {toast} from 'react-toastify';
import {
  extractErrorMessageFromApolloError,
  propagateErrorsContext,
} from '../util/errors';

export interface UpdateOrganizationSubscriptionSeatModalProps {
  open: boolean;
  subscriptionID: string | undefined;
  subscriptionCurrentSeats: number | undefined;
  stripeLink: string | null;
  onUpdate(): void;
  onClose(): void;
}

const UpdateOrganizationSubscriptionSeatModal: React.FC<UpdateOrganizationSubscriptionSeatModalProps> = makeComp(
  props => {
    const [hasAcknowledged, setHasAcknowledged] = useState(false);
    const [newSeats, setNewSeats] = useState<number | null>(null);
    const [loading, setLoading] = useState(false);

    const [
      updateOrganizationSubscriptionMutation,
    ] = useUpdateOrganizationSubscriptionMutation({
      context: propagateErrorsContext(),
    });

    const onCloseWrapper = () => {
      setNewSeats(null);
      setHasAcknowledged(false);
      props.onClose();
    };

    const isValidSeats = (seats: number | null) => {
      return !!(seats && Number.isInteger(seats) && seats > 0);
    };

    const handleUpdate = async (
      event: React.MouseEvent<HTMLButtonElement, MouseEvent>,
      data: ButtonProps
    ) => {
      setLoading(true);
      if (props.subscriptionID && isValidSeats(newSeats)) {
        try {
          const updateSeatResult = await updateOrganizationSubscriptionMutation(
            {
              variables: {
                organizationSubscriptionID: props.subscriptionID,
                seats: newSeats!,
              },
            }
          );

          if (updateSeatResult.errors) {
            toast(`Error updating seats: ${updateSeatResult.errors}`);
          } else {
            setNewSeats(null);
            setHasAcknowledged(false);
            props.onUpdate();
          }
        } catch (err) {
          const errMsg = extractErrorMessageFromApolloError(err) ?? err.message;
          toast(`Error updating seats: ${errMsg}`);
        }
      }
      setLoading(false);
    };

    const handleAcknowledgement = (
      event: React.FormEvent<HTMLInputElement>,
      data: CheckboxProps
    ) => {
      setHasAcknowledged(!!data.checked);
    };

    const handleNewSeatInput = (
      event: React.ChangeEvent<HTMLInputElement>,
      data: InputOnChangeData
    ) => {
      if (data.value === '') {
        setNewSeats(null);
      } else {
        const inputSeats = parseInt(data.value, 10);
        if (isValidSeats(inputSeats)) {
          setNewSeats(inputSeats);
        } else {
          setNewSeats(null);
        }
      }
    };

    return (
      <Modal
        open={props.open && props.subscriptionID != null}
        onClose={onCloseWrapper}>
        <Header>Update Seat Count</Header>
        <Modal.Content>
          <Form size="large">
            <Form.Field inline>
              <label>Number of Seats</label>
              <Input
                data-test="seat-count-input"
                value={newSeats ?? ''}
                placeholder={`${props.subscriptionCurrentSeats} (Current)`}
                onChange={handleNewSeatInput}
              />
            </Form.Field>
            <Form.Field inline>
              <label>
                I understand that this change modifies the maximum number of
                allowed seats, but does not change the number of{' '}
                <i>billed seats</i> for the organization. Therefore, I take
                responsibility to update Stripe, or other applicable billing
                system, accordingly.
                {props.stripeLink && (
                  <span>
                    <br />
                    <br />
                    Organization's Stripe link:{' '}
                    <a
                      href={props.stripeLink}
                      target="_blank"
                      rel="noopener noreferrer">
                      {props.stripeLink}
                    </a>
                  </span>
                )}
                <br />
                <br />
              </label>
              <label>Acknowledgement:&nbsp;&nbsp;</label>
              <Checkbox
                data-test="seat-count-input"
                placeholder="Seat Count Acknowledgement"
                onChange={handleAcknowledgement}
              />
            </Form.Field>
          </Form>
        </Modal.Content>
        <Modal.Actions>
          <Button content="Cancel" onClick={onCloseWrapper} />
          <Button
            data-test="update-seats-btn"
            primary
            loading={loading}
            disabled={loading || !hasAcknowledged || !isValidSeats(newSeats)}
            onClick={handleUpdate}>
            Update Seat Count
          </Button>
        </Modal.Actions>
      </Modal>
    );
  },
  {
    id: 'UpdateOrganizationSubscriptionSeatModal',
    memo: true,
  }
);

export default UpdateOrganizationSubscriptionSeatModal;
